import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { useLang } from "../i18n/LangContext";

export const Hero = () => {
  const { ui } = useLang();
  // Όποιος είδε ήδη την παρουσίαση (ή πάτησε home), πάει κατευθείαν στη δίστηλη οθόνη
  const seenOnLoad = useRef(sessionStorage.getItem("heroIntroSeen") === "1").current;
  const [loaded, setLoaded] = useState(false);
  const [ended, setEnded] = useState(seenOnLoad);
  const [captionIdx, setCaptionIdx] = useState(-1);
  const [captionHidden, setCaptionHidden] = useState(seenOnLoad);
  const [runId, setRunId] = useState(0);
  const [fading, setFading] = useState(false);
  useEffect(() => setLoaded(true), []);

  // Τέλος παρουσίασης: η οθόνη σβήνει απαλά και μετά αποκαλύπτεται η δίστηλη διάταξη
  const finish = () => {
    sessionStorage.setItem("heroIntroSeen", "1");
    setCaptionHidden(true);
    setFading(true);
    setTimeout(() => setEnded(true), 1000);
    setTimeout(() => setFading(false), 2400);
  };

  // Ακολουθία τίτλων: πρώτα ο χάρτης μόνος του, μετά τα μηνύματα (2" αργότερα, κάθε 5").
  // Ο τελικός τίτλος εμφανίζεται με 1" επιπλέον καθυστέρηση. Στο τέλος κρύβονται.
  useEffect(() => {
    if (runId === 0 && seenOnLoad) return;
    setCaptionIdx(-1);
    setCaptionHidden(false);
    setEnded(false);
    const last = ui.heroVideoCaptions.length - 1;
    const timers = ui.heroVideoCaptions.map((_, i) =>
      setTimeout(() => setCaptionIdx(i), 2000 + i * 5000 + (i === last ? 1000 : 0))
    );
    timers.push(setTimeout(() => setCaptionIdx(ui.heroVideoCaptions.length), 2000 + ui.heroVideoCaptions.length * 5000 + 1000));
    timers.push(setTimeout(finish, 2000 + (ui.heroVideoCaptions.length + 1) * 5000 + 1000));
    return () => timers.forEach(clearTimeout);
  }, [ui, runId]);

  const replay = () => {
    setRunId((r) => r + 1);
  };

  // Παράλειψη παρουσίασης — κατευθείαν στη δίστηλη οθόνη, χωρίς κίνηση
  const skip = () => {
    sessionStorage.setItem("heroIntroSeen", "1");
    setCaptionIdx(ui.heroVideoCaptions.length + 1);
    setCaptionHidden(true);
    setEnded(true);
  };

  const options = [
    { to: "/synopsi", label: ui.heroOptOverview, testid: "hero-opt-overview" },
    { to: "/analysi", label: ui.heroOptAnalysis, testid: "hero-opt-analysis" },
    { to: "/axiologisi", label: ui.heroOptBoard, testid: "hero-opt-board" },
    { to: "/test", label: ui.heroOptQuiz, testid: "hero-opt-quiz" },
    { to: "/tekmiiria", label: ui.heroOptArt, testid: "hero-opt-art" },
    { to: "/dimosieuseis", label: ui.heroOptPubs, testid: "hero-opt-pubs" },
    { to: "/programma", label: ui.heroOptProgram, testid: "hero-opt-program" },
    { to: "/sxetika", label: ui.aboutTitle, testid: "hero-opt-about" },
  ];

  return (
    <header data-testid="hero" className="min-h-screen lg:flex">
      {/* Σκοτείνιασμα κατά την αλλαγή οθόνης */}
      <div className={`pointer-events-none fixed inset-0 z-40 bg-anthra transition-opacity duration-1000 ${fading ? 'opacity-100' : 'opacity-0'}`} />
      {/* Αριστερή στήλη — εμφανίζεται ακαριαία πίσω από το σκοτάδι */}
      <div
        className={`relative overflow-hidden bg-anthra ${
          ended ? 'lg:w-[52%] lg:opacity-100' : 'hidden lg:block lg:w-0 lg:opacity-0'
        }`}
      >
        <div className={`flex h-full flex-col justify-between px-6 pb-6 pt-12 md:px-12 lg:w-[52vw] lg:pt-14 ${ended ? 'animate-fade-up' : ''}`} style={ended ? { animationDelay: '0.9s' } : undefined}>
        <div />

        <div className="my-6">
          <p
            className={`mb-4 text-xs md:text-sm uppercase tracking-[0.28em] font-semibold text-paper/60 ${loaded ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.18s' }}
          >
            {ui.heroEyebrow}
          </p>
          <h1
            data-testid="hero-title"
            className={`font-serif text-5xl md:text-6xl xl:text-7xl font-medium leading-[0.98] tracking-tight text-paper ${loaded ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.25s' }}
          >
            {ui.heroTitle}
          </h1>
          <nav
            data-testid="hero-options"
            aria-label="Κεντρικές επιλογές"
            className={`mt-8 max-w-2xl border-t border-paper/20 ${loaded ? 'animate-fade-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.55s' }}
          >
            {options.map((o, i) => (
              <Link
                key={o.to}
                to={o.to}
                data-testid={o.testid}
                className="group flex items-baseline gap-5 border-b border-paper/20 py-3 transition-colors duration-200 hover:bg-paper/5 md:py-3.5"
              >
                <span className="font-serif text-lg text-terra md:text-xl">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-serif text-lg text-paper transition-colors duration-200 group-hover:text-terra md:text-xl">
                  {o.label}
                </span>
              </Link>
            ))}
          </nav>
        </div>

        <div
          data-testid="hero-credit"
          className={`mt-4 flex flex-col items-center gap-2 text-center ${loaded ? 'animate-fade-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.85s' }}
        >
          <a href="https://novuscodex.gr" target="_blank" rel="noopener noreferrer" data-testid="novus-logo-hero-link" aria-label="NOVUS CODEX — sponsor website" className="shrink-0">
            <img src="/images/novus-codex-logo-white.png" alt="NOVUS CODEX" className="h-14 w-auto transition-transform duration-200 hover:scale-105 md:h-16" data-testid="novus-logo-hero" />
          </a>
          <p className="max-w-md text-[11px] leading-relaxed text-paper/70">
            {ui.heroCredit1}<br />
            {ui.heroCredit2} <span className="font-semibold text-paper/90">{ui.authorName}</span>
          </p>
        </div>
        </div>
      </div>

      {/* Δεξιά στήλη — στην αρχή μόνο η κίνηση του χάρτη και τα μηνύματα σε όλη την οθόνη· μετά γλιστρά δεξιά. Στο mobile μετά το τέλος ο χάρτης αφαιρείται. */}
      <div className={`relative overflow-hidden ${ended ? 'hidden lg:block lg:flex-1' : 'flex-1 min-h-screen'}`} style={{ backgroundColor: '#E7DDC6', backgroundImage: "url('/images/dulichium-delisle-1794.jpg')", backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <img
          key={runId}
          src="/images/dulichium-delisle-1794.jpg"
          alt="Detail of Delisle's 1794 map: 'Leucadia Peninsula', 'Dulichium' at the position of present-day Ithaca, 'Cephalenia', 'Zacynthus'"
          data-testid="hero-image"
          className={`${seenOnLoad && runId === 0 ? '' : 'animate-hero-zoom-out'} absolute inset-0 h-full w-full object-cover object-center`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-aegean/50 via-transparent to-transparent" />
        <div className={`pointer-events-none absolute inset-x-0 top-0 h-44 bg-gradient-to-b from-anthra/75 via-anthra/35 to-transparent transition-opacity duration-700 ${captionHidden ? 'opacity-0' : 'opacity-100'}`} />
        <div className={`pointer-events-none absolute inset-0 bg-anthra/25 transition-opacity duration-700 ${captionHidden ? 'opacity-0' : 'opacity-100'}`} />
        <div
          data-testid="hero-video-caption"
          className={`pointer-events-none absolute inset-0 flex items-start justify-center px-6 pt-[30vh] transition-opacity duration-700 ${captionHidden ? 'opacity-0' : 'opacity-100'}`}
        >
          {captionIdx >= 0 && (captionIdx === ui.heroVideoCaptions.length ? (
            <div key={captionIdx} data-testid="hero-video-sponsor" className="animate-fade-up flex flex-col items-center gap-4 text-center">
              <span
                className="text-xs font-semibold uppercase tracking-[0.28em] text-paper md:text-base"
                style={{ textShadow: '0 1px 8px rgba(0,0,0,0.8), 0 0 32px rgba(0,0,0,0.6)' }}
              >
                {ui.heroVideoSponsor}
              </span>
              <img
                src="/images/novus-codex-logo-white.png"
                alt="NOVUS CODEX"
                className="h-14 w-auto md:h-24"
                style={{ filter: 'drop-shadow(0 1px 10px rgba(0,0,0,0.75))' }}
              />
            </div>
          ) : (
            <p
              key={captionIdx}
              className={`animate-fade-up font-serif font-semibold leading-snug text-paper ${captionIdx === ui.heroVideoCaptions.length - 1 ? 'w-full max-w-none text-center text-4xl md:text-8xl' : 'max-w-2xl text-center text-2xl md:text-5xl'}`}
              style={{ textShadow: '0 1px 8px rgba(0,0,0,0.8), 0 0 32px rgba(0,0,0,0.6)' }}
            >
              {captionIdx === ui.heroVideoCaptions.length - 1 ? (
                <>
                  <span className="block leading-none">{ui.heroVideoTitleA}</span>
                  <span className="block leading-none">{ui.heroVideoTitleB}</span>
                </>
              ) : (
                ui.heroVideoCaptions[captionIdx]
              )}
            </p>
          ))}
        </div>
        {!ended && (
          <button
            type="button"
            onClick={skip}
            data-testid="hero-skip"
            className="absolute bottom-8 left-1/2 z-20 -translate-x-1/2 rounded-full border border-paper/40 bg-anthra/55 px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.18em] text-paper backdrop-blur-md transition-colors duration-200 hover:bg-anthra/80"
          >
            {ui.heroSkipLabel}
          </button>
        )}
      </div>
      {/* Επανάληψη παρουσίασης — δεξιά, κάτω από το κουμπί ήχου, κάθετα κεντραρισμένη (fixed — εκτός του container του χάρτη ώστε να μένει ορατή και στο mobile) */}
      <div className="fixed right-4 top-[calc(50%+3.25rem)] z-50 flex -translate-y-1/2 flex-col items-center gap-2">
        {ended && (
          <button
            type="button"
            onClick={replay}
            data-testid="hero-video-replay"
            aria-label={ui.heroReplayLabel}
            title={ui.heroReplayLabel}
            className="rounded-full border border-paper/40 bg-anthra/55 p-2.5 text-paper backdrop-blur-md transition-transform duration-200 hover:scale-110"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        )}
      </div>
    </header>
  );
};
